import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Sparkles,
    ShieldCheck,
    ArrowRight,
    Trophy,
} from "lucide-react";

/**
 * /join?ref=CODE — landing page reached from a referral QR / link.
 *
 * We validate the code, greet the visitor with the referrer's name, then
 * hand them off to /signup?ref=CODE where the field is prefilled.
 */
export default function Join() {
    const [params] = useSearchParams();
    const rawCode = (params.get("ref") || "").trim().toUpperCase();
    const navigate = useNavigate();

    const [state, setState] = useState({ status: "checking" });

    useEffect(() => {
        if (!rawCode) {
            setState({ status: "no_code" });
            return;
        }
        api.get(`/public/referrals/${encodeURIComponent(rawCode)}`)
            .then((r) => {
                if (r.data?.valid) {
                    setState({
                        status: "valid",
                        code: r.data.code,
                        referrer: r.data.referrer_name,
                    });
                    // Fire-and-forget click log, deduped per (code + IP +
                    // day) server-side and per-session client-side so tab
                    // reloads don't triple-count.
                    const key = `perx_ref_click_${r.data.code}`;
                    if (!sessionStorage.getItem(key)) {
                        sessionStorage.setItem(key, "1");
                        api.post(
                            `/public/referrals/${encodeURIComponent(r.data.code)}/click`
                        ).catch(() => {});
                    }
                } else {
                    setState({ status: "invalid" });
                }
            })
            .catch(() => setState({ status: "invalid" }));
    }, [rawCode]);

    const goSignup = () => {
        const code = state.status === "valid" ? state.code : "";
        navigate(code ? `/signup?ref=${encodeURIComponent(code)}` : "/signup");
    };

    return (
        <div className="min-h-screen bg-cream flex flex-col">
            <header className="px-6 lg:px-12 py-6 flex items-center justify-between">
                <Link to="/" className="font-display text-2xl text-navy-700">
                    Perx <span className="text-gold-600">Group</span>
                </Link>
                <Link
                    to="/login"
                    data-testid="join-login-link"
                    className="text-sm text-navy-500 hover:text-navy-700 underline underline-offset-4"
                >
                    Already a member? Sign in
                </Link>
            </header>

            <main className="flex-1 px-6 lg:px-12 pb-20 flex items-center">
                <div
                    data-testid="join-page"
                    className="max-w-3xl mx-auto w-full bg-white rounded-3xl border border-navy-100 shadow-[0_20px_80px_rgba(0,28,80,0.08)] overflow-hidden"
                >
                    <div className="p-8 sm:p-12">
                        {state.status === "checking" && (
                            <div className="text-navy-500 text-sm py-24 text-center">
                                Loading your invite…
                            </div>
                        )}

                        {state.status === "valid" && (
                            <>
                                <div className="inline-flex items-center gap-1.5 rounded-full bg-gold-100 text-navy-700 border border-gold-300 px-3 py-1 text-[11px] font-semibold tracking-wide uppercase">
                                    <Trophy size={12} className="text-gold-600" />
                                    You've been invited
                                </div>
                                <h1
                                    data-testid="join-title"
                                    className="font-display text-4xl sm:text-5xl text-navy-700 mt-4 leading-tight"
                                >
                                    <span className="text-navy-700">
                                        {state.referrer}
                                    </span>{" "}
                                    thinks you'll love Perx.
                                </h1>
                                <p className="text-navy-500 mt-4 text-base leading-relaxed max-w-xl">
                                    Perx is a members-only marketplace for exclusive deals
                                    across insurance, travel, lifestyle, health and finance.
                                    Your referral code{" "}
                                    <span
                                        data-testid="join-code"
                                        className="font-mono tracking-widest text-navy-700 bg-cream px-2 py-0.5 rounded"
                                    >
                                        {state.code}
                                    </span>{" "}
                                    is pre-applied.
                                </p>

                                <div className="mt-8 grid sm:grid-cols-2 gap-3">
                                    <Benefit
                                        icon={Sparkles}
                                        title="Every deal unlocked"
                                        body="Vendor-vetted offers you can't get on the public web."
                                    />
                                    <Benefit
                                        icon={ShieldCheck}
                                        title="Panda Promise"
                                        body="30-day refund if you find a better price."
                                    />
                                </div>

                                <div className="mt-10 flex flex-col sm:flex-row gap-3">
                                    <Button
                                        onClick={goSignup}
                                        data-testid="join-cta"
                                        className="rounded-full bg-navy-700 hover:bg-navy-800 text-cream h-12 px-8 text-base"
                                    >
                                        Create my account
                                        <ArrowRight size={16} className="ml-2" />
                                    </Button>
                                    <Link to="/pricing">
                                        <Button
                                            variant="outline"
                                            className="rounded-full h-12 px-8 border-navy-200 text-navy-700"
                                        >
                                            See what's included
                                        </Button>
                                    </Link>
                                </div>
                            </>
                        )}

                        {(state.status === "invalid" || state.status === "no_code") && (
                            <>
                                <h1 className="font-display text-3xl text-navy-700">
                                    {state.status === "no_code"
                                        ? "Join Perx"
                                        : "That invite link doesn't work."}
                                </h1>
                                <p className="text-navy-500 mt-3 text-sm">
                                    {state.status === "no_code"
                                        ? "Create your account below to start unlocking member pricing."
                                        : "The code may have expired or been mistyped. You can still create a Perx account — you just won't be linked to the person who invited you."}
                                </p>
                                <div className="mt-8 flex gap-3">
                                    <Button
                                        onClick={goSignup}
                                        data-testid="join-cta-fallback"
                                        className="rounded-full bg-navy-700 hover:bg-navy-800 text-cream h-12 px-8"
                                    >
                                        Continue to signup
                                        <ArrowRight size={16} className="ml-2" />
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}

function Benefit({ icon: Icon, title, body }) {
    return (
        <div className="rounded-xl bg-cream border border-navy-100 p-4">
            <div className="flex items-center gap-2 text-navy-700 font-semibold text-sm">
                <Icon size={14} className="text-gold-600" />
                {title}
            </div>
            <p className="text-xs text-navy-500 mt-1 leading-relaxed">{body}</p>
        </div>
    );
}
